import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Breadcrumb } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { SourceBadge } from "@/components/SourceBadge";
import { ConfidencePill } from "@/components/ConfidencePill";
import { InvoiceDocumentViewer } from "@/components/InvoiceDocumentViewer";
import { HouseholdCategoryField } from "@/components/husholdning/HouseholdCategoryField";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { SOURCES, PAYMENT_TYPES, AUDIT_ACTIONS } from "@/lib/constants";
import { formatKr, formatDate, formatDateTime, formatAccount, formatOrg, copyText, todayIso, daysLabel } from "@/lib/format";
import { toast } from "sonner";
import { Copy, CheckCircle2, ShieldAlert, AlertTriangle, Pencil, Save, X, Trash2, Undo2, Files, History, Landmark, Info, ExternalLink, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const FieldRow = ({ label, value, confidence, mono }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b last:border-b-0">
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {label}
      <ConfidencePill value={confidence} />
    </div>
    <div className={cn("text-sm font-medium text-right", mono && "tnum")}>{value || <span className="text-muted-foreground italic">Mangler</span>}</div>
  </div>
);

const EditField = ({ id, label, confidence, children, hint }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <Label htmlFor={id}>{label}</Label>
      <ConfidencePill value={confidence} />
    </div>
    {children}
    {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite, isOwner, actAs } = useAuth();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ paid_at: todayIso(), note: "" });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busyAction, setBusyAction] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/invoices/${id}`);
      setInv(res.data);
      setForm({
        issuer_name: res.data.issuer_name || "",
        issuer_org_number: res.data.issuer_org_number || "",
        invoice_number: res.data.invoice_number || "",
        amount: res.data.amount ?? "",
        currency: res.data.currency || "NOK",
        invoice_date: res.data.invoice_date || "",
        due_date: res.data.due_date || "",
        account_number: res.data.account_number || "",
        kid: res.data.kid || "",
        description: res.data.description || "",
        payment_type: res.data.payment_type || "faktura",
        source: res.data.source || "opplasting",
        category_id: res.data.category_id || "ingen",
        notes: res.data.notes || "",
      });
    } catch (e) {
      setError(errorMessage(e, "Fant ikke fakturaen"));
    }
  }, [id]);

  const loadHistory = useCallback(() => {
    api.get(`/invoices/${id}/history`).then((r) => setHistory(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    load();
    loadHistory();
    api.get("/categories").then((r) => setCategories(r.data.items)).catch(() => {});
  }, [load, loadHistory]);

  useEffect(() => {
    if (inv && canWrite && (inv.status === "ma_kontrolleres") && !editing) setEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv?.id]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const conf = inv?.field_confidence || {};

  const copy = async (value, label) => {
    if (!value) return;
    const ok = await copyText(value);
    if (ok) toast.success(`${label} kopiert`); else toast.error("Kunne ikke kopiere");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, category_id: form.category_id === "ingen" ? null : form.category_id, amount: form.amount === "" ? null : Number(String(form.amount).replace(/\s/g, "").replace(",", ".")) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const res = await api.put(`/invoices/${id}`, payload);
      setInv((prev) => ({ ...prev, ...res.data }));
      setEditing(false);
      toast.success("Endringer lagret");
      loadHistory();
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke lagre"));
    } finally {
      setSaving(false);
    }
  };

  const action = async (key, fn, successMsg) => {
    setBusyAction(key);
    try {
      const res = await fn();
      if (res?.data) setInv((prev) => ({ ...prev, ...res.data, duplicate_invoices: res.data.duplicates?.length ? prev.duplicate_invoices : [] }));
      if (successMsg) toast.success(successMsg);
      loadHistory();
      window.dispatchEvent(new Event("rp:refresh-notifications"));
      return res;
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    } finally {
      setBusyAction("");
    }
  };

  const confirmData = async () => {
    if (editing) {
      setSaving(true);
      try {
        const payload = { ...form, category_id: form.category_id === "ingen" ? null : form.category_id, amount: form.amount === "" ? null : Number(String(form.amount).replace(/\s/g, "").replace(",", ".")) };
        Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
        await api.put(`/invoices/${id}`, payload);
      } catch (e) {
        toast.error(errorMessage(e, "Kunne ikke lagre"));
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    const res = await action("confirm", () => api.post(`/invoices/${id}/confirm`), "Opplysningene er bekreftet");
    if (res) { setEditing(false); load(); }
  };

  const markPaid = async () => {
    const res = await action("pay", () => api.post(`/invoices/${id}/pay`, { paid_at: payForm.paid_at, note: payForm.note || null }), "Faktura markert som betalt");
    if (res) setPayOpen(false);
  };

  const deleteInvoice = async () => {
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Faktura slettet");
      navigate("/fakturaer");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const mergeKeepThis = () => action("merge", () => api.post(`/invoices/${id}/duplicate/merge`), "Duplikater slått sammen – denne fakturaen er beholdt").then((r) => r && load());
  const mergeKeepOther = (otherId) => action("merge", () => api.post(`/invoices/${id}/duplicate/merge`, null, { params: { keep_id: otherId } }), "Slått sammen – den eksisterende fakturaen er beholdt").then((r) => r && navigate(`/fakturaer/${otherId}`));
  const dismissDup = () => action("dismiss", () => api.post(`/invoices/${id}/duplicate/dismiss`), "Markert som ikke duplikat").then((r) => r && load());

  if (error) return <EmptyState icon={AlertTriangle} title="Fant ikke fakturaen" description={error} actionLabel="Til fakturaer" actionTo="/fakturaer" />;
  if (!inv) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-12 w-96" />
        <div className="grid lg:grid-cols-[3fr_2fr] gap-4"><Skeleton className="h-[520px]" /><Skeleton className="h-[520px]" /></div>
      </div>
    );
  }

  const status = inv.display_status || inv.status;
  const isPaid = inv.status === "betalt";
  const isRejected = inv.status === "avvist";
  const needsConfirm = inv.status === "ma_kontrolleres" || inv.status === "mulig_duplikat";
  const highFlags = (inv.flags || []).filter((f) => f.level === "høy");

  const DetailsPanel = (
    <div className="space-y-4">
      {!canWrite ? (
        <Alert className="actas-banner border" data-testid="readonly-alert">
          <Info className="h-4 w-4" />
          <AlertTitle>Kun innsyn</AlertTitle>
          <AlertDescription>Du kan se denne regningen for {actAs?.owner_name}, men ikke endre, bekrefte eller markere den som betalt.</AlertDescription>
        </Alert>
      ) : null}

      {isRejected ? (
        <Alert className="bg-secondary">
          <Info className="h-4 w-4" />
          <AlertTitle>Denne fakturaen er avvist som duplikat</AlertTitle>
          <AlertDescription>
            Den er slått sammen med en annen faktura. {inv.merged_into ? <Link to={`/fakturaer/${inv.merged_into}`} className="text-primary underline" data-testid="merged-into-link">Åpne fakturaen som ble beholdt</Link> : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {(inv.flags || []).map((f) => (
        <Alert key={f.code} className={f.level === "høy" ? "status-suspicious border" : "status-forfaller_snart border"} data-testid={`flag-${f.code}`}>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between gap-2">
            {f.title}
            {canWrite ? <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => action(`flag-${f.code}`, () => api.post(`/invoices/${id}/flags/${f.code}/dismiss`), "Advarsel avvist")} data-testid={`flag-dismiss-${f.code}`}>Jeg har sjekket</Button> : null}
          </AlertTitle>
          <AlertDescription>{f.detail}{f.code === "kontonummer_endret" ? " Verifiser alltid nye kontonummer direkte med leverandøren før du betaler." : ""}</AlertDescription>
        </Alert>
      ))}

      {inv.status === "mulig_duplikat" && inv.duplicate_invoices?.length ? (
        <Card className="border-[#E2CCFF] dark:border-[#6D28D9]/50" data-testid="duplicate-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Files className="h-4 w-4 text-dup" /> Mulig duplikat</CardTitle>
            <CardDescription>Denne regningen ligner på {inv.duplicate_invoices.length === 1 ? "en regning" : `${inv.duplicate_invoices.length} regninger`} du allerede har. Kontroller før du betaler.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {inv.duplicate_invoices.map((d) => {
              const reason = (inv.duplicates || []).find((x) => x.invoice_id === d.id);
              return (
                <div key={d.id} className="rounded-lg border bg-secondary p-3" data-testid={`duplicate-candidate-${d.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{d.issuer_name || "Ukjent"}</p>
                      <p className="text-xs text-muted-foreground">{formatKr(d.amount, d.currency)} · forfall {formatDate(d.due_date)} · {SOURCES[d.source] || d.source}</p>
                      {reason ? <p className="text-xs mt-1 text-dup">{reason.reason} ({reason.certainty} sikkerhet)</p> : null}
                    </div>
                    <StatusBadge status={d.display_status || d.status} />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" asChild><Link to={`/fakturaer/${d.id}`} data-testid={`duplicate-open-${d.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1" /> Åpne</Link></Button>
                    {canWrite ? <Button size="sm" variant="outline" disabled={!!busyAction} onClick={() => mergeKeepOther(d.id)} data-testid={`duplicate-keep-other-${d.id}`}>Behold denne, avvis ny</Button> : null}
                  </div>
                </div>
              );
            })}
            {canWrite ? (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button disabled={!!busyAction} onClick={mergeKeepThis} data-testid="invoice-merge-duplicate-button">{busyAction === "merge" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Files className="h-4 w-4 mr-2" />} Slå sammen – behold denne</Button>
                <Button variant="outline" disabled={!!busyAction} onClick={dismissDup} data-testid="invoice-reject-duplicate-button">Ikke duplikat – behold begge</Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {inv.status === "ma_kontrolleres" ? (
        <Alert className="status-ma_kontrolleres border" data-testid="needs-review-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Kontroller opplysningene</AlertTitle>
          <AlertDescription>
            {inv.warnings?.length ? <ul className="list-disc pl-4 mt-1">{inv.warnings.map((w) => <li key={w}>{w}</li>)}</ul> : "Vi var usikre på ett eller flere felt. Sjekk mot dokumentet, rett opp og bekreft."}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Betalingsinformasjon */}
      {!isRejected && (
        <Card data-testid="payment-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> Betalingsinformasjon</CardTitle>
            <CardDescription>{inv.payment_type === "avtalegiro" ? "Trekkes automatisk via AvtaleGiro. Du trenger ikke betale manuelt." : "Kopier og lim inn i nettbanken. Marker som betalt når du er ferdig."}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: "Kontonummer", value: formatAccount(inv.account_number), raw: inv.account_number, testId: "invoice-account-number-copy-button" },
              { label: "KID", value: inv.kid, raw: inv.kid, testId: "invoice-kid-copy-button" },
              { label: "Beløp", value: formatKr(inv.amount, inv.currency), raw: inv.amount !== null && inv.amount !== undefined ? Number(inv.amount).toFixed(2).replace(".", ",") : null, testId: "invoice-amount-copy-button" },
              { label: "Forfallsdato", value: formatDate(inv.due_date), raw: formatDate(inv.due_date), testId: "invoice-due-copy-button" },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-2 rounded-lg border bg-secondary px-3 py-2">
                <div>
                  <p className="text-xs text-muted-foreground">{row.label}</p>
                  <p className="font-medium tnum" data-testid={`payment-${row.label.toLowerCase()}`}>{row.raw ? row.value : <span className="text-muted-foreground italic">Mangler</span>}</p>
                </div>
                <Button variant="outline" size="sm" disabled={!row.raw} onClick={() => copy(row.raw, row.label)} data-testid={row.testId}><Copy className="h-3.5 w-3.5 mr-1" /> Kopier</Button>
              </div>
            ))}
            {canWrite ? (
              <div className="flex flex-wrap gap-2 pt-2">
                {isPaid ? (
                  <Button variant="outline" disabled={!!busyAction} onClick={() => action("unpay", () => api.post(`/invoices/${id}/unpay`), "Betaling angret")} data-testid="invoice-unpay-button"><Undo2 className="h-4 w-4 mr-2" /> Angre betalt</Button>
                ) : (
                  <Button onClick={() => setPayOpen(true)} disabled={!!busyAction} data-testid="invoice-mark-paid-button"><CheckCircle2 className="h-4 w-4 mr-2" /> Marker som betalt</Button>
                )}
              </div>
            ) : null}
            {isPaid ? <p className="text-sm text-muted-foreground pt-1">Betalt {formatDateTime(inv.paid_at)}{inv.paid_note ? ` · ${inv.paid_note}` : ""}</p> : null}
          </CardContent>
        </Card>
      )}

      {/* Fakturadetaljer */}
      <Card data-testid="details-card">
        <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Fakturadetaljer</CardTitle>
            <CardDescription>{inv.extraction_method === "ehf" ? "Lest fra EHF-fil (100 % nøyaktig)" : inv.extraction_method === "manuell" ? "Registrert manuelt" : inv.overall_confidence !== null && inv.overall_confidence !== undefined ? `Automatisk lest · samlet sikkerhet ${Math.round(inv.overall_confidence * 100)} %` : ""}</CardDescription>
          </div>
          {!isRejected && canWrite && (editing ? (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); load(); }} data-testid="invoice-cancel-edit-button"><X className="h-4 w-4 mr-1" /> Avbryt</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="invoice-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Lagre</Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="invoice-edit-button"><Pencil className="h-4 w-4 mr-1" /> Rediger</Button>
          ))}
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <EditField id="issuer_name" label="Leverandør" confidence={conf.issuer_name}><Input id="issuer_name" value={form.issuer_name} onChange={(e) => setForm({ ...form, issuer_name: e.target.value })} data-testid="edit-issuer-name" /></EditField>
              <EditField id="issuer_org_number" label="Organisasjonsnummer" confidence={conf.issuer_org_number}><Input id="issuer_org_number" value={form.issuer_org_number} onChange={(e) => setForm({ ...form, issuer_org_number: e.target.value })} inputMode="numeric" data-testid="edit-org-number" /></EditField>
              <EditField id="invoice_number" label="Fakturanummer" confidence={conf.invoice_number}><Input id="invoice_number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="edit-invoice-number" /></EditField>
              <EditField id="amount" label="Beløp" confidence={conf.amount}><Input id="amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} inputMode="decimal" className="tnum" data-testid="edit-amount" /></EditField>
              <EditField id="invoice_date" label="Fakturadato" confidence={conf.invoice_date}><Input id="invoice_date" type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="edit-invoice-date" /></EditField>
              <EditField id="due_date" label="Forfallsdato" confidence={conf.due_date}><Input id="due_date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="edit-due-date" /></EditField>
              <EditField id="account_number" label="Kontonummer" confidence={conf.account_number} hint="11 siffer"><Input id="account_number" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} inputMode="numeric" className="tnum" data-testid="edit-account-number" /></EditField>
              <EditField id="kid" label="KID" confidence={conf.kid}><Input id="kid" value={form.kid} onChange={(e) => setForm({ ...form, kid: e.target.value })} inputMode="numeric" className="tnum" data-testid="edit-kid" /></EditField>
              <EditField id="payment_type" label="Betalingstype">
                <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                  <SelectTrigger id="payment_type" data-testid="edit-payment-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{Object.entries(PAYMENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </EditField>
              <EditField id="source" label="Kilde">
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger id="source" data-testid="edit-source"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{Object.entries(SOURCES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </EditField>
              <EditField id="category_id" label="Kategori">
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger id="category_id" data-testid="edit-category"><SelectValue placeholder="Velg kategori" /></SelectTrigger>
                  <SelectContent className="bg-card"><SelectItem value="ingen">Uten kategori</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </EditField>
              <EditField id="description" label="Beskrivelse"><Input id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-description" /></EditField>
              <div className="sm:col-span-2"><EditField id="notes" label="Egne notater"><Textarea id="notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-notes" /></EditField></div>
            </div>
          ) : (
            <div>
              <FieldRow label="Leverandør" value={inv.issuer_name} confidence={conf.issuer_name} />
              <FieldRow label="Organisasjonsnummer" value={inv.issuer_org_number ? formatOrg(inv.issuer_org_number) : null} confidence={conf.issuer_org_number} mono />
              <FieldRow label="Fakturanummer" value={inv.invoice_number} confidence={conf.invoice_number} mono />
              <FieldRow label="Beløp" value={inv.amount !== null && inv.amount !== undefined ? formatKr(inv.amount, inv.currency) : null} confidence={conf.amount} mono />
              <FieldRow label="Fakturadato" value={inv.invoice_date ? formatDate(inv.invoice_date) : null} confidence={conf.invoice_date} mono />
              <FieldRow label="Forfallsdato" value={inv.due_date ? formatDate(inv.due_date) : null} confidence={conf.due_date} mono />
              <FieldRow label="Kontonummer" value={inv.account_number ? formatAccount(inv.account_number) : null} confidence={conf.account_number} mono />
              <FieldRow label="KID" value={inv.kid} confidence={conf.kid} mono />
              <FieldRow label="Betalingstype" value={PAYMENT_TYPES[inv.payment_type] || inv.payment_type} />
              <FieldRow label="Kilde" value={SOURCES[inv.source] || inv.source} />
              <FieldRow label="Kategori" value={inv.category_id && catMap[inv.category_id] ? catMap[inv.category_id].name : null} />
              <FieldRow label="Beskrivelse" value={inv.description} />
              {inv.customer_name ? <FieldRow label="Mottaker" value={inv.customer_name} /> : null}
              {inv.notes ? <FieldRow label="Notater" value={inv.notes} /> : null}
              {inv.email_meta ? <FieldRow label="Mottatt fra e-post" value={`${inv.email_meta.from || ""}${inv.email_meta.subject ? ` – ${inv.email_meta.subject}` : ""}`} /> : null}
            </div>
          )}
          {/* Regnskap: kategori i virksomhetens kontoplan (standard via Regninger-kategorien, kan overstyres). Uten kategori telles regningen som «Ukategorisert». */}
          {!isRejected ? (
            <div className="mt-5 rounded-lg border bg-secondary/40 p-3">
              <HouseholdCategoryField source="regning" docId={inv.id} value={inv.household_category_id || null} regningerCategoryId={editing ? (form.category_id === "ingen" ? null : form.category_id) : inv.category_id} onChanged={(r) => { setInv((p) => ({ ...p, household_category_id: r.category_id })); loadHistory(); }} compact />
            </div>
          ) : null}
          {needsConfirm && !isRejected && canWrite ? (
            <div className="mt-5 rounded-lg border bg-accent p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-accent-foreground">Stemmer opplysningene med dokumentet? Bekreft for å flytte regningen til «Til betaling».</p>
              <Button onClick={confirmData} disabled={!!busyAction || saving} data-testid="invoice-confirm-data-button">{busyAction === "confirm" || saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Bekreft opplysningene</Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Historikk */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Historikk</CardTitle>
          <CardDescription>Alle visninger og endringer logges.</CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? <p className="text-sm text-muted-foreground">Ingen hendelser ennå.</p> : (
            <ol className="space-y-2" data-testid="invoice-history">
              {history.slice(0, 12).map((h) => (
                <li key={h.id} className="flex items-center justify-between gap-3 text-sm">
                  <span>{AUDIT_ACTIONS[h.action] || h.action}{h.meta?.fields?.length ? <span className="text-muted-foreground"> · {h.meta.fields.join(", ")}</span> : ""}</span>
                  <span className="text-xs text-muted-foreground tnum shrink-0">{formatDateTime(h.created_at)}</span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {isOwner ? (
        <div className="flex justify-end">
          <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)} data-testid="invoice-delete-button"><Trash2 className="h-4 w-4 mr-2" /> Slett faktura</Button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "Fakturaer", to: "/fakturaer" }, { label: inv.issuer_name || "Faktura" }]} />

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <StatusBadge status={status} size="md" />
            <SourceBadge source={inv.source} />
            {highFlags.length ? <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium status-suspicious"><ShieldAlert className="h-3.5 w-3.5" /> Kontroller</span> : null}
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight truncate" data-testid="invoice-title">{inv.issuer_name || "Ukjent avsender"}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {inv.invoice_number ? `Faktura ${inv.invoice_number} · ` : ""}{inv.due_date ? `Forfall ${formatDate(inv.due_date)}` : "Forfallsdato mangler"}{inv.days_to_due !== null && !isPaid && !isRejected ? ` · ${daysLabel(inv.days_to_due)}` : ""}
          </p>
        </div>
        <div className="text-left md:text-right">
          <p className="font-heading text-3xl font-semibold tnum" data-testid="invoice-amount">{formatKr(inv.amount, inv.currency)}</p>
          <p className="text-xs text-muted-foreground">Registrert {formatDateTime(inv.created_at)}</p>
        </div>
      </div>

      <div className="hidden lg:block">
        <ResizablePanelGroup direction="horizontal" className="min-h-[640px] rounded-xl">
          <ResizablePanel defaultSize={55} minSize={30}><div className="h-full pr-3"><InvoiceDocumentViewer invoiceId={inv.id} document={inv.document} /></div></ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={45} minSize={30}><div className="h-full pl-3 overflow-auto">{DetailsPanel}</div></ResizablePanel>
        </ResizablePanelGroup>
      </div>
      <div className="lg:hidden">
        <Tabs defaultValue="detaljer">
          <TabsList className="w-full grid grid-cols-2 bg-card border">
            <TabsTrigger value="detaljer" data-testid="mobile-tab-details">Detaljer</TabsTrigger>
            <TabsTrigger value="dokument" data-testid="mobile-tab-document">Dokument</TabsTrigger>
          </TabsList>
          <TabsContent value="detaljer" className="mt-4">{DetailsPanel}</TabsContent>
          <TabsContent value="dokument" className="mt-4"><div className="h-[70vh]"><InvoiceDocumentViewer invoiceId={inv.id} document={inv.document} /></div></TabsContent>
        </Tabs>
        {!isRejected && canWrite && (
          <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-card/95 backdrop-blur px-4 py-3 flex items-center gap-2 sm:hidden" data-testid="mobile-action-bar">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground truncate">{inv.issuer_name || "Ukjent avsender"}</p>
              <p className="font-semibold tnum whitespace-nowrap">{formatKr(inv.amount, inv.currency)}</p>
            </div>
            {needsConfirm ? (
              <Button onClick={confirmData} disabled={!!busyAction || saving} data-testid="mobile-confirm-button"><CheckCircle2 className="h-4 w-4 mr-2" /> Bekreft</Button>
            ) : isPaid ? (
              <Button variant="outline" onClick={() => action("unpay", () => api.post(`/invoices/${id}/unpay`), "Betaling angret")} disabled={!!busyAction} data-testid="mobile-unpay-button"><Undo2 className="h-4 w-4 mr-2" /> Angre betalt</Button>
            ) : (
              <Button onClick={() => setPayOpen(true)} disabled={!!busyAction} data-testid="mobile-pay-button"><CheckCircle2 className="h-4 w-4 mr-2" /> Marker betalt</Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Marker som betalt</DialogTitle>
            <DialogDescription>{inv.issuer_name} – {formatKr(inv.amount, inv.currency)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="paid_at">Betalingsdato</Label><Input id="paid_at" type="date" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} data-testid="pay-date-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="paid_note">Notat (valgfritt)</Label><Input id="paid_note" placeholder="f.eks. Betalt i DNB nettbank" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} data-testid="pay-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Avbryt</Button>
            <Button onClick={markPaid} disabled={!!busyAction} data-testid="pay-confirm-button">{busyAction === "pay" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Bekreft betalt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette fakturaen?</AlertDialogTitle>
            <AlertDialogDescription>Fakturaen og dokumentet slettes permanent. Dette kan ikke angres.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={deleteInvoice} data-testid="invoice-delete-confirm-button">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
